import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { flatten } from 'lodash';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { AppMainComponent } from '../../app.main.component';
import { BrandingService } from '../../services/branding.service';
import { PermissionService } from '../../services/permission.service';
import {
  BUSINESS_CATEGORIES,
  BUSINESS_PAGES,
  BusinessPageEntry,
  getBusinessPage,
  isBusinessPageVisible,
} from '../business-pages-list';

interface CategoryGroup {
  meta: typeof BUSINESS_CATEGORIES[number];
  pages: BusinessPageEntry[];
}

@Component({
  selector: 'app-business',
  standalone: true,
  templateUrl: './business.component.html',
  styleUrl: './business.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    RouterOutlet,
    BreadcrumbModule,
    OverlayPanelModule,
    InputTextModule,
  ],
})
export class BusinessComponent implements OnInit, OnDestroy {

  home: MenuItem = { icon: 'pi pi-home', routerLink: '/home' };

  breadcrumb: MenuItem[] = [];

  visiblePages: BusinessPageEntry[] = [];
  filteredPages: BusinessPageEntry[] = [];
  categoryGroups: CategoryGroup[] = [];
  pageSearch = '';
  currentPageSlug = '';
  currentPageName = '';
  currentPageIcon = '';

  showIconGrid = true;

  @ViewChild('pagePanel') pagePanel: any;
  @ViewChild('pageSearchInput') pageSearchInput: ElementRef;

  private subs = new SubSink();

  constructor(
    private branding: BrandingService,
    private permissionService: PermissionService,
    private router: Router,
    private appMain: AppMainComponent,
  ) {}

  ngOnInit(): void {
    this.recomputePages();
    this.watchRoute();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private recomputePages(): void {
    const allPermissions = flatten(BUSINESS_PAGES.map((p) => p.requiresPermissions));
    const currentZoneType$ = this.branding.currentZone().pipe(map((z) => z.type));
    const permissions$ = this.permissionService.watchPermissions(allPermissions);

    this.subs.sink = combineLatest([currentZoneType$, permissions$])
      .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
      .subscribe(([zoneType, permissions]) => {
        this.visiblePages = BUSINESS_PAGES.filter((page) => {
          const hasPermission = page.requiresPermissions.every(
            (p) => permissions[allPermissions.indexOf(p)],
          );
          return isBusinessPageVisible(page, zoneType, () => hasPermission);
        });
        this.filteredPages = [...this.visiblePages];
        this.buildCategoryGroups();
        this.updateBreadcrumb();
        this.appMain.cd.detectChanges();
      });
  }

  private buildCategoryGroups(): void {
    this.categoryGroups = BUSINESS_CATEGORIES
      .map((meta) => ({
        meta,
        pages: this.visiblePages.filter((p) => p.category === meta.id),
      }))
      .filter((g) => g.pages.length > 0);
  }

  private watchRoute(): void {
    const url$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url),
      distinctUntilChanged(),
    );

    this.subs.sink = url$
      .pipe(startWith(this.router.url))
      .subscribe((url) => this.readCurrentPage(url));
  }

  private readCurrentPage(url: string): void {
    const parts = url.split('?')[0].split('/');
    const child = parts[2];

    if (child) {
      this.currentPageSlug = child;
      const entry = getBusinessPage(child);
      this.currentPageName = entry?.name ?? child;
      this.currentPageIcon = entry?.icon ?? '';
      this.showIconGrid = false;
    } else {
      this.currentPageSlug = '';
      this.currentPageName = '';
      this.currentPageIcon = '';
      this.showIconGrid = true;
    }

    this.updateBreadcrumb();
  }

  private updateBreadcrumb(): void {
    if (this.showIconGrid) {
      this.breadcrumb = [{ label: '\u00A0Business' }];
    } else {
      this.breadcrumb = [
        { label: 'Business', routerLink: '/business' },
        ...(this.currentPageName ? [{
          label: `\u00A0${this.currentPageName}`,
          icon: this.currentPageIcon,
          styleClass: 'business-breadcrumb-link',
          command: (e: { originalEvent: Event }) => this.showPagePicker(e.originalEvent),
        }] : []),
      ];
    }
  }

  showPagePicker(event?: Event): void {
    this.pageSearch = '';
    this.filteredPages = [...this.visiblePages];
    const target = (event?.target as HTMLElement) ?? null;
    this.pagePanel?.toggle(event, target);
    setTimeout(() => this.pageSearchInput?.nativeElement?.focus(), 50);
  }

  filterPages(): void {
    const q = this.pageSearch.toLowerCase().trim();
    this.filteredPages = q
      ? this.visiblePages.filter((p) => p.name.toLowerCase().includes(q))
      : [...this.visiblePages];
  }

  onPageSelect(slug: string): void {
    if (slug) {
      this.router.navigate(['/business', slug]);
    }
    this.pagePanel?.hide();
  }
}